// SPDX-License-Identifier: MIT
/** Colour helpers for building tonal ramps from a brand palette. */
export type RGB = [
    number,
    number,
    number
];
export function hexToRgb(hex: string): RGB {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
export function rgbToHex([r, g, b]: RGB): string {
    const h = (v: number) => Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`.toUpperCase();
}
/** Mix two hex colours (t = 0 → a, 1 → b). */
export function mix(a: string, b: string, t: number): string {
    const x = hexToRgb(a);
    const y = hexToRgb(b);
    return rgbToHex([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]);
}
/** Relative luminance (WCAG). */
export function luminance(hex: string): number {
    const lin = (v: number) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const [r, g, b] = hexToRgb(hex);
    return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}
/** WCAG contrast ratio between two colours (1–21). Body copy wants ≥ 4.5, display type ≥ 3. */
export function contrast(a: string, b: string): number {
    const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [
        number,
        number
    ];
    return (hi + 0.05) / (lo + 0.05);
}
/** Pick whichever of `options` reads best on `background`. */
export function readableOn(background: string, options: string[]): string {
    return options.reduce((best, c) => (contrast(c, background) > contrast(best, background) ? c : best));
}
